<template>
  <div class="upload-queue">
    <!-- PDF Processing Status -->
    <div v-if="pdfProcessingStatus" class="pdf-processing-banner">
      <div class="loading-spinner"></div>
      <span>{{ pdfProcessingStatus }}</span>
    </div>

    <!-- Bulgarian Invoice Progress Bar -->
    <div v-if="bulgarianInvoiceProgress.show" class="bulgarian-progress-overlay">
      <div class="bulgarian-progress-card">
        <div class="progress-header">
          <LineIcon name="file-text" :size="15" :stroke-width="1.8" class="progress-icon" />
          <h3>Processing Bulgarian Gas Station Invoices</h3>
        </div>
        <div class="progress-info">
          <p class="progress-filename">{{ bulgarianInvoiceProgress.fileName }}</p>
          <p class="progress-count">
            Creating invoice {{ bulgarianInvoiceProgress.current }} of {{ bulgarianInvoiceProgress.total }}
          </p>
        </div>
        <div class="progress-bar-container">
          <div
            class="progress-bar-fill"
            :style="{ width: `${(bulgarianInvoiceProgress.current / bulgarianInvoiceProgress.total) * 100}%` }"
          ></div>
        </div>
        <div class="progress-percentage">
          {{ Math.round((bulgarianInvoiceProgress.current / bulgarianInvoiceProgress.total) * 100) }}%
        </div>
      </div>
    </div>

    <!-- Bulgarian Invoice Modal -->
    <BulgarianInvoiceModal
      :show="showBulgarianModal"
      :bulgarianData="pendingBulgarianData"
      :processing="processingBulgarian"
      @confirm="handleBulgarianConfirm"
      @cancel="handleBulgarianCancel"
    />

    <!-- Bulgarian Invoice Success Modal -->
    <div v-if="showBulgarianSuccessModal" class="modal-overlay" @click.self="closeBulgarianSuccessModal">
      <div class="modal-content bulgarian-success-modal">
        <div class="modal-header">
          <div class="success-icon-wrapper">
            <LineIcon name="check-circle" :size="15" :stroke-width="1.8" class="success-icon" />
          </div>
          <h3>Invoices Created Successfully!</h3>
          <button @click="closeBulgarianSuccessModal" class="btn btn-icon">
            <LineIcon name="x" :size="15" :stroke-width="1.8" />
          </button>
        </div>
        <div class="modal-body">
          <div class="success-summary">
            <div class="summary-row">
              <span class="summary-label">File:</span>
              <span class="summary-value">{{ bulgarianSuccessData.fileName }}</span>
            </div>
            <div class="summary-row success-row">
              <span class="summary-label">
                <LineIcon name="check-circle" :size="15" :stroke-width="1.8" />
                Successfully Created:
              </span>
              <span class="summary-value success-value">{{ bulgarianSuccessData.successCount }}</span>
            </div>
            <div v-if="bulgarianSuccessData.failCount > 0" class="summary-row error-row">
              <span class="summary-label">
                <LineIcon name="alert-circle" :size="15" :stroke-width="1.8" />
                Failed:
              </span>
              <span class="summary-value error-value">{{ bulgarianSuccessData.failCount }}</span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeBulgarianSuccessModal" class="btn-confirm-success">
            <LineIcon name="check" :size="15" :stroke-width="1.8" />
            OK
          </button>
        </div>
      </div>
    </div>

    <!-- Toggle Upload Form Button (only show when upload form is hidden) -->
    <button
      v-if="!showUploadForm"
      @click="showUploadForm = true"
      class="btn-toggle-form"
    >
      <LineIcon name="plus" :size="14" :stroke-width="1.8" />
      {{ $t('upload.showUploadForm') }}
    </button>

    <!-- The company and document type were picked here as well as in the bar
         above the table. One page, one place to choose: the upload now takes
         whatever that bar is set to, through selectedCompanyId. -->

    <!-- Hard Scan Mode Checkbox -->
    <div v-if="showUploadForm" class="hard-scan-option"
    style="display: none;"
    >
      <label class="checkbox-label">
        <input type="checkbox" v-model="isHardScanMode" :disabled="!canUseHardScan" />
        <span class="checkbox-text">
          <LineIcon name="star" :size="15" :stroke-width="1.8" />
          {{ $t('upload.hardScanMode') }}
        </span>
      </label>
      <p class="checkbox-description">
        {{ $t('upload.hardScanDesc', { remaining: hardScansRemaining, limit: hardScanLimit }) }}
      </p>
    </div>

    <!-- The way out, above the thing it closes.
         It used to sit under the drop zone and over the upload buttons,
         where it read as one of them. -->
    <button
      v-if="showUploadForm"
      @click="showUploadForm = false"
      class="btn-hide-form"
    >
      <LineIcon name="minus" :size="14" :stroke-width="1.8" />
      {{ $t('upload.hideUploadForm') }}
    </button>

    <!-- Drag and Drop Zone OR Limit Reached Message.
         The class is for the page around it: it lets the zone be stretched to
         the height of the cards beside it. -->
    <div v-if="showUploadForm" class="upload-zone-slot">
      <!-- Limit Reached Message -->
      <div v-if="hasReachedLimit" class="limit-reached-message">
        <h3 class="limit-title">{{ $t('upload.limitReachedTitle') }}</h3>
        <p class="limit-description">{{ $t('upload.limitReachedDesc') }}</p>
        <div class="limit-details">
          <p>{{ $t('upload.scansUsed', { used: authStore.subscription?.scans_used || 0, limit: authStore.subscription?.scan_limit || 0 }) }}</p>
        </div>
        <router-link to="/subscription" class="btn-upgrade">
          <LineIcon name="arrow-up" :size="15" :stroke-width="1.8" />
          {{ $t('upload.upgradeNow') }}
        </router-link>
      </div>

      <!-- Upload Zone (when not at limit) -->
      <div
        v-else
        ref="uploadZoneRef"
        class="upload-zone"
        :class="{ 'drag-over': isDragging, 'disabled': pdfProcessingStatus }"
        @drop.prevent="handleDrop"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @click="!pdfProcessingStatus && triggerFileInput()"
      >
        <div class="upload-zone-content">
          <div class="upload-icon">
            <LineIcon name="camera" class="upload-icon-glyph" :size="40" :stroke-width="1.5" />
          </div>
          <p class="upload-text">{{ uploadText }}</p>
          <p class="upload-subtext">{{ $t('upload.supportedFormats') }}</p>
        </div>
        <input
          ref="fileInput"
          type="file"
          accept="image/*,.pdf,.doc,.docx"
          multiple
          @change="handleFileSelect"
          class="hidden"
        />
      </div>
    </div>

    <!-- Hidden multi-select input for uploading a multi-page invoice register.
         Kept OUTSIDE the upload-zone so its programmatic click() does not bubble
         to the zone's @click handler and open a second file dialog. -->
    <input
      ref="registerInput"
      type="file"
      accept="image/*,application/pdf"
      multiple
      @change="handleRegisterSelect"
      class="hidden"
    />

    <!-- Multi-Page PDF Modal.
         Built like the dashboard: a plain white card, hairline borders, the
         same drawn icons, and the three choices as rows — a mark, what it
         does, and what that means. -->
    <div v-if="showMultiPageModal" class="modal-overlay" @click.self="closeMultiPageModal">
      <div class="modal-content modal-choice">
        <div class="modal-header">
          <div class="header-content">
            <h3>{{ $t('upload.multiPage') }}</h3>
            <p class="modal-subtitle">{{ $t('upload.multiPageDesc', { count: pendingPdfInfo.pageCount }) }}</p>
          </div>
          <button @click="closeMultiPageModal" class="modal-close" :aria-label="$t('common.close')">
            <LineIcon name="x" :size="16" :stroke-width="1.8" />
          </button>
        </div>
        <div class="modal-body">
          <div class="modal-options">
            <button @click="handleMultiPageChoice('single')" class="modal-option-btn single-invoice" :disabled="processingPdfChoice">
              <span class="option-icon">
                <LineIcon name="file-text" :size="18" :stroke-width="1.8" />
              </span>
              <span class="option-text">
                <h4>{{ $t('upload.singleInvoice') }}</h4>
                <p>{{ $t('upload.combinePagesDesc') }}</p>
              </span>
            </button>
            <button @click="handleMultiPageChoice('multiple')" class="modal-option-btn multiple-invoices" :disabled="processingPdfChoice">
              <span class="option-icon">
                <LineIcon name="copy" :size="18" :stroke-width="1.8" />
              </span>
              <span class="option-text">
                <h4>{{ $t('upload.multipleInvoices') }}</h4>
                <p>{{ $t('upload.separatePagesDesc', { count: pendingPdfInfo.pageCount }) }}</p>
              </span>
            </button>
            <button @click="handleMultiPageChoice('mixed')" class="modal-option-btn mixed-invoices" :disabled="processingPdfChoice">
              <span class="option-icon">
                <LineIcon name="layers" :size="18" :stroke-width="1.8" />
              </span>
              <span class="option-text">
                <h4>{{ $t('upload.mixedMode') }}</h4>
                <p>{{ $t('upload.mixedModeDesc') }}</p>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Mixed Mode Classification Modal.
         The dashboard's dress: hairline borders, drawn icons, quiet controls.
         The page previews keep their size — they are the thing being judged
         here, so everything around them got smaller instead. -->
    <div v-if="showMixedModeModal" class="modal-overlay" @click.self="closeMixedModeModal">
      <div class="modal-content mixed-mode-modal">
        <div class="modal-header">
          <div class="header-content">
            <h3>{{ $t('upload.classifyPages') }}</h3>
            <p class="modal-subtitle">{{ $t('upload.assignPageDesc') }}</p>
          </div>
          <button @click="closeMixedModeModal" class="modal-close" :aria-label="$t('common.close')">
            <LineIcon name="x" :size="16" :stroke-width="1.8" />
          </button>
        </div>
        <div class="modal-body mixed-mode-body">
          <div class="pages-classification-grid">
            <div
              v-for="(page, index) in mixedModePages"
              :key="index"
              class="page-classification-item"
              :class="{ 'page-excluded': !page.included }"
            >
              <div class="page-preview-wrapper">
                <div class="page-checkbox-wrapper" @click.stop>
                  <input
                    type="checkbox"
                    v-model="page.included"
                    class="page-checkbox"
                    :aria-label="`${$t('upload.page')} ${index + 1}`"
                  />
                </div>
                <img :src="page.preview" :alt="`${$t('upload.page')} ${index + 1}`" class="page-preview-img" :style="{ transform: `rotate(${page.rotation || 0}deg)` }" />
                <div class="page-number-badge">{{ $t('upload.page') }} {{ index + 1 }}</div>
                <div class="page-rotation-controls">
                  <button @click.stop="rotatePage(index, -90)" class="btn-rotate" :title="$t('upload.rotateLeft')">
                    <LineIcon name="rotate-left" :size="13" :stroke-width="1.8" />
                  </button>
                  <button @click.stop="rotatePage(index, 90)" class="btn-rotate" :title="$t('upload.rotateRight')">
                    <LineIcon name="rotate-right" :size="13" :stroke-width="1.8" />
                  </button>
                </div>
              </div>
              <div class="page-classification-controls">
                <span class="classification-label">{{ $t('upload.invoiceNumber') }}</span>
                <!-- The app's own dropdown, not the browser's: the native popup
                     arrives in its own design and ignores this stylesheet. -->
                <button
                  type="button"
                  class="invoice-pick"
                  :class="{ open: openInvoiceFor === index }"
                  :disabled="!page.included"
                  @click.stop="toggleInvoiceMenu(index, $event)"
                  :title="`${$t('upload.invoice')} ${page.invoiceNumber}`"
                >
                  <!-- The number alone: the field is named by the label beside
                       it, and repeating the word here cost the card the one
                       thing it had to show — on a narrower screen the word
                       filled the box and the number was the part cut off. -->
                  {{ page.invoiceNumber }}
                </button>
              </div>
            </div>
          </div>

          <div class="classification-summary">
            <h4>{{ $t('upload.summary') }}</h4>
            <div class="summary-items">
              <div v-for="(pages, invoiceNum) in groupedPagesSummary" :key="invoiceNum" class="summary-item">
                <LineIcon name="file-text" :size="13" :stroke-width="1.8" />
                <strong>{{ $t('upload.invoice') }} {{ invoiceNum }}:</strong>
                <span>{{ pages.length }} {{ pages.length > 1 ? $t('upload.pages') : $t('upload.page') }} ({{ pages.map(p => p + 1).join(', ') }})</span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeMixedModeModal" class="btn-cancel-mixed">{{ $t('common.cancel') }}</button>
          <button @click="confirmMixedModeClassification" class="btn-confirm-mixed" :disabled="processingMixedMode">
            <LineIcon v-if="!processingMixedMode" name="check" :size="14" :stroke-width="1.8" />
            <div v-else class="loading-spinner-small"></div>
            {{ processingMixedMode ? $t('upload.processing') : $t('upload.confirmUpload') }}
          </button>
        </div>
      </div>
    </div>

    <!-- The invoice-number menu, in the body: the page cards clip what
         overflows them, and one menu serves whichever card is open. -->
    <Teleport to="body">
      <div v-if="openInvoiceFor !== null" class="invoice-menu" :style="invoiceMenuStyle">
        <button
          v-for="num in maxInvoiceNumbers"
          :key="num"
          type="button"
          class="invoice-menu-item"
          :class="{ selected: num === openInvoiceValue }"
          @click="chooseInvoiceNumber(num)"
        >
          {{ $t('upload.invoice') }} {{ num }}
        </button>
        <button
          type="button"
          class="invoice-menu-item invoice-menu-item--new"
          @click="chooseInvoiceNumber(maxInvoiceNumbers + 1)"
        >
          + {{ $t('upload.newInvoice') }}
        </button>
      </div>
    </Teleport>

    <!-- Upload Buttons -->
    <div v-if="showUploadForm" class="upload-buttons" v-show="uploadQueue.length === 0">
      <!-- Photographing a document is a phone's job, so the button is only
           there on one. On a desktop it opened the same file picker as the
           button beside it, under a name that promised a camera.
           On a phone it first asks WHICH document is being shot — a single
           page or a multi-page register. -->
      <button v-if="isMobileDevice" @click="showMobileDocChooser = true" class="btn-camera">
        <LineIcon name="camera" :size="17" :stroke-width="2" />
        {{ $t('upload.takePhoto') }}
      </button>
      <button @click="triggerFileInput" class="btn-upload btn-upload--primary">
        <LineIcon name="upload" :size="17" :stroke-width="2" />
        {{ $t('upload.uploadFile') }}
      </button>
      <button v-if="registerEnabled" @click="triggerRegisterInput" class="btn-upload" :disabled="registerUploading">
        <LineIcon name="table" :size="17" :stroke-width="2" />
        {{ registerUploading ? $t('upload.processing') : 'Качи регистър' }}
      </button>
    </div>

    <!-- Mobile: choose what kind of document is being photographed.
         Teleported to <body> so the fixed overlay is centred on the true viewport,
         never trapped/offset by a transformed ancestor. -->
    <Teleport to="body">
      <div v-if="showMobileDocChooser" class="doc-chooser-overlay" @click.self="showMobileDocChooser = false">
        <div class="doc-chooser">
          <h3 class="doc-chooser-title">Какъв документ качвате?</h3>
          <button type="button" class="doc-choice" @click="chooseDocType('single')">
            <LineIcon name="camera" :size="15" :stroke-width="1.8" class="doc-choice-icon" />
            <span class="doc-choice-text">
              <span class="doc-choice-name">Едностранична фактура</span>
              <span class="doc-choice-desc">Една снимка — един документ</span>
            </span>
          </button>
          <button type="button" class="doc-choice" @click="chooseDocType('multi')">
            <LineIcon name="camera" :size="15" :stroke-width="1.8" class="doc-choice-icon" />
            <span class="doc-choice-text">
              <span class="doc-choice-name">Многостранична фактура</span>
              <span class="doc-choice-desc">Няколко снимки → една фактура</span>
            </span>
          </button>
          <button type="button" class="doc-chooser-cancel" @click="showMobileDocChooser = false">Отказ</button>
        </div>
      </div>
    </Teleport>

    <!-- Multi-page photo staging (mobile): each shot is one page of ONE invoice -->
    <div v-if="isMultiPageMode && multiPageFiles.length > 0" class="multipage-panel">
      <div class="multipage-header">
        <span class="multipage-title">Многостранична фактура — {{ multiPageFiles.length }} стр.</span>
        <button type="button" class="multipage-close" @click="cancelMultiPage" aria-label="Отказ">
          <LineIcon name="x" :size="15" :stroke-width="1.8" />
        </button>
      </div>
      <div class="multipage-pages">
        <div v-for="(item, index) in multiPageFiles" :key="index" class="multipage-page">
          <img :src="item.preview" alt="" />
          <span class="multipage-page-num">{{ index + 1 }}</span>
          <button type="button" class="multipage-remove" @click="removePage(index)" aria-label="Премахни">
            <LineIcon name="x" :size="15" :stroke-width="1.8" />
          </button>
        </div>
      </div>
      <div class="multipage-actions">
        <button type="button" class="multipage-btn multipage-btn-add" @click="captureMultiPagePhoto" :disabled="processingMultiPage">
          <LineIcon name="plus" :size="15" :stroke-width="1.8" />
          Добави
        </button>
        <button type="button" class="multipage-btn multipage-btn-done" @click="confirmMultiPageUpload" :disabled="processingMultiPage || multiPageFiles.length === 0">
          <LineIcon name="check" :size="15" :stroke-width="1.8" />
          {{ processingMultiPage ? $t('upload.processing') : $t('common.done') + ' (' + multiPageFiles.length + ')' }}
        </button>
      </div>
    </div>

    <!-- Register upload status -->
    <div v-if="registerStatus" class="pdf-processing-banner">
      <div class="loading-spinner"></div>
      <span>{{ registerStatus }}</span>
    </div>

    <!-- File Queue List -->
    <div v-if="uploadQueue.length > 0" class="file-queue">
      <div class="queue-header">
        <h3>{{ $t('upload.uploadQueue', { count: uploadQueue.length }) }}</h3>
        <button @click="clearCompleted" class="btn-clear" v-if="hasCompleted">
          {{ $t('upload.clearCompleted') }}
        </button>
      </div>

      <div class="file-list">
        <template v-for="item in uploadQueue" :key="item.id">
          <!-- Consolidated Success Card (when completed) -->
          <!-- A finished file, as one row: the mark that it is done, what was
               read off it, and what can be done with it. It was a 300px card
               with the four fields in a striped table and two full-size
               buttons under them. -->
          <div
            v-if="item.status === 'completed' && item.scanData"
            class="success-card-consolidated"
          >
            <!-- Original Invoice Thumbnail -->
            <div class="original-thumbnail" @click="item.scanId && showOriginal(item.scanId)" :style="item.scanId ? 'cursor: pointer;' : ''">
              <img
                v-if="item.scanId"
                :src="`/api/invoices/${item.scanId}/original?thumbnail=true`"
                :alt="item.file.name"
                class="thumbnail-image"
                @error="handleThumbnailError"
              />
              <div v-else class="thumbnail-placeholder">
                <LineIcon name="file-text" :size="16" :stroke-width="1.8" />
              </div>
            </div>

            <!-- Success Info -->
            <div class="success-info">
              <div class="success-head">
                <LineIcon name="check" :size="13" :stroke-width="2" class="success-check" />
                <span class="success-number">{{ item.scanData?.invoice_number || $t('upload.processedSuccess') }}</span>
                <span v-if="item.scanData?.seller_name" class="success-seller">{{ item.scanData.seller_name }}</span>
              </div>
              <div class="success-meta">
                <span v-if="item.scanData?.grand_total" class="success-amount">
                  {{ item.scanData.grand_total }} {{ item.scanData.currency }}
                </span>
                <span v-if="item.scanData?.invoice_date">{{ item.scanData.invoice_date }}</span>
              </div>
            </div>

            <div class="success-actions">
              <button @click="viewInvoice(item.scanId)" class="btn-queue-action btn-view-invoice" :title="$t('upload.viewDetails')">
                <LineIcon name="pencil" :size="13" :stroke-width="1.8" />
              </button>
              <button @click="openRescanModal(item.scanId)" class="btn-queue-action btn-rescan" :title="$t('rescan.rescanButton')">
                <LineIcon name="rotate-right" :size="13" :stroke-width="1.8" />
              </button>
              <button @click="removeCompletedItem(item.id)" class="btn-queue-action" :title="$t('common.close')">
                <LineIcon name="x" :size="13" :stroke-width="1.8" />
              </button>
            </div>
          </div>

          <!-- Processing Item (when not completed) -->
          <div
            v-else
            class="file-item"
            :class="{
              'uploading': item.status === 'uploading',
              'processing': item.status === 'processing',
              'completed': item.status === 'completed',
              'failed': item.status === 'failed'
            }"
          >
            <!-- File Preview -->
            <div class="file-preview">
              <img v-if="item.preview" :src="item.preview" :alt="item.file.name" />
              <div v-else class="file-icon">
                <LineIcon name="file-text" :size="16" :stroke-width="1.8" />
              </div>
            </div>

            <!-- File Info -->
            <div class="file-info">
              <!-- Name and size share a line: two lines for what a row says in
                   one was what made five uploads taller than the page. -->
              <div class="file-head">
                <span class="file-name">{{ item.file.name }}</span>
                <span class="file-size">{{ formatFileSize(item.file.size) }}</span>
              </div>

              <!-- Progress Bar for Uploading -->
              <div v-if="item.status === 'uploading' || item.status === 'queued'" class="upload-state">
                <div class="processing-text">
                  <span class="loading-spinner"></span>
                  {{ $t('upload.uploading', { time: item.processingTime }) }}
                </div>
                <div class="progress-bar">
                  <div class="progress-fill" :style="{ width: item.progress + '%' }">
                    <div class="scanning-line"></div>
                  </div>
                </div>
              </div>

              <!-- Processing State -->
              <div v-if="item.status === 'processing'" class="processing-state">
                <div class="processing-text">
                  <span class="loading-spinner"></span>
                  {{ $t('upload.processing') }} {{ item.processingTime }}s
                </div>
                <div class="progress-bar">
                  <div class="progress-fill processing" :style="{ width: item.processingProgress + '%' }">
                    <div class="scanning-line"></div>
                  </div>
                </div>
              </div>

              <!-- Status Messages -->
              <div v-if="item.status === 'failed'" class="status-error">
                {{ item.error || $t('upload.uploadFailed') }}
              </div>
            </div>

            <!-- Actions -->
            <div class="file-actions">
              <button
                v-if="item.status === 'failed'"
                @click="retryUpload(item.id)"
                class="btn-queue-action btn-retry"
                :title="$t('upload.uploadFailed')"
              >
                <LineIcon name="rotate-right" :size="13" :stroke-width="1.8" />
              </button>
              <button
                v-if="item.status !== 'completed'"
                @click="cancelUpload(item.id)"
                class="btn-queue-action"
                :title="$t('common.cancel')"
              >
                <LineIcon name="x" :size="13" :stroke-width="1.8" />
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Rescan Modal -->
    <RescanModal
      :show="showRescanModal"
      :loading="rescanLoading"
      :error="rescanError"
      :canUseHardScan="canUseHardScan"
      :hardScansRemaining="hardScansRemaining"
      :hardScanLimit="hardScanLimit"
      @close="closeRescanModal"
      @rescan="handleRescan"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import LineIcon from '@/components/icons/LineIcon.vue';
import { isMobileDevice } from '@/utils/device';
import { useI18n } from 'vue-i18n';
import { useScanStore } from '@/stores/scan';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';
import { useRouter } from 'vue-router';
import { extractPdfPages, isPDF, getPdfPageCount } from '@/utils/pdfProcessor';
import { processBulgarianGasStationPdf, convertToInvoiceFormat } from '@/utils/pdfBulgarianGasStationsPreProcessor';
import { useCompanies } from '@/composables/useCompanyVendor';
import RescanModal from '@/components/modals/RescanModal.vue';
import BulgarianInvoiceModal from '@/components/modals/BulgarianInvoiceModal.vue';
import { useRescan } from '@/composables/useRescan';
import { validateCompanyOnInvoice } from '@/services/exportValidationService';
import api from '@/utils/api';

const { t } = useI18n();

const props = defineProps({
  selectedCompanyId: {
    type: Number,
    default: null
  },
  companyName: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['scan-completed', 'register-completed', 'company-change', 'form-visibility']);

const scanStore = useScanStore();
const authStore = useAuthStore();
const uiStore = useUiStore();
const router = useRouter();

// Rescan functionality
const {
  showRescanModal,
  rescanLoading,
  rescanError,
  hardScansRemaining,
  hardScanLimit,
  canUseHardScan,
  openRescanModal,
  closeRescanModal,
  performRescan
} = useRescan();

const uploadText = computed(() => {
  return props.companyName
    ? t('upload.dragDropFor', { company: props.companyName })
    : t('upload.dragDrop');
});

// Check if user has reached scan limit
const hasReachedLimit = computed(() => {
  if (!authStore.subscription) return false;
  return authStore.scansRemaining <= 0;
});

// PDF processing state
const pdfProcessingStatus = ref('');

// Bulgarian invoice processing state
const showBulgarianModal = ref(false);
const pendingBulgarianData = ref(null);
const pendingBulgarianFile = ref(null);
const processingBulgarian = ref(false);
const bulgarianInvoiceProgress = ref({
  show: false,
  current: 0,
  total: 0,
  fileName: ''
});
const showBulgarianSuccessModal = ref(false);
const bulgarianSuccessData = ref({
  successCount: 0,
  failCount: 0,
  fileName: ''
});

// Company and type selection using composables
const { companies, loadCompanies } = useCompanies();
const selectedCompanyId = ref(props.selectedCompanyId);
const selectedType = ref(null);

// Multi-page mode state
const isMultiPageMode = ref(false);
const isHardScanMode = ref(false);
const multiPageFiles = ref([]);
const processingMultiPage = ref(false);

// Multi-page PDF modal state
const showMultiPageModal = ref(false);
const pendingPdfInfo = ref({ file: null, pageCount: 0 });
/* Its own flag, not the camera's: the two are unrelated pieces of work and
   sharing one meant either could leave the other's buttons dead. */
const processingPdfChoice = ref(false);

// Mixed mode state
const showMixedModeModal = ref(false);
const mixedModePages = ref([]);
const processingMixedMode = ref(false);
const maxInvoiceNumbers = ref(1);

const groupedPagesSummary = computed(() => {
  const groups = {};
  // Only include pages that are checked
  mixedModePages.value.filter(page => page.included).forEach((page, index) => {
    const invoiceNum = page.invoiceNumber;
    if (!groups[invoiceNum]) {
      groups[invoiceNum] = [];
    }
    groups[invoiceNum].push(index);
  });
  return groups;
});

const updateInvoiceNumbers = () => {
  // Update max invoice numbers based on selections
  const maxSelected = Math.max(...mixedModePages.value.map(p => p.invoiceNumber));
  maxInvoiceNumbers.value = maxSelected;
};

/*
 * Which invoice a page belongs to, picked from the app's own menu.
 *
 * One menu for the whole grid, placed under whichever card was pressed and
 * teleported to the body — the cards clip what overflows them, and twenty
 * copies of the same list would otherwise be in the page at once.
 */
const openInvoiceFor = ref(null);
const openInvoiceValue = ref(1);
const invoiceMenuStyle = ref({});

const closeInvoiceMenu = () => {
  openInvoiceFor.value = null;
};

const toggleInvoiceMenu = (index, event) => {
  if (openInvoiceFor.value === index) {
    closeInvoiceMenu();
    return;
  }

  const box = event.currentTarget.getBoundingClientRect();
  /* Downwards by default, upwards when the card is near the bottom of the
     screen — the grid scrolls and the last row sits on the footer. */
  const menuHeight = Math.min(240, (maxInvoiceNumbers.value + 1) * 28 + 8);
  const below = window.innerHeight - box.bottom;

  invoiceMenuStyle.value = {
    top: below < menuHeight + 12
      ? `${Math.max(8, box.top - menuHeight - 4)}px`
      : `${box.bottom + 4}px`,
    left: `${Math.max(8, Math.min(box.left, window.innerWidth - box.width - 8))}px`,
    minWidth: `${Math.max(box.width, 120)}px`,
  };
  openInvoiceValue.value = mixedModePages.value[index]?.invoiceNumber || 1;
  openInvoiceFor.value = index;
};

const chooseInvoiceNumber = (num) => {
  const index = openInvoiceFor.value;
  closeInvoiceMenu();

  if (index === null || !mixedModePages.value[index]) {
    return;
  }

  mixedModePages.value[index].invoiceNumber = num;
  updateInvoiceNumbers();
};

const handleInvoiceMenuOutside = (event) => {
  if (openInvoiceFor.value === null) return;
  if (event.target.closest('.invoice-menu') || event.target.closest('.invoice-pick')) return;
  closeInvoiceMenu();
};

const handleInvoiceMenuKey = (event) => {
  if (event.key === 'Escape') closeInvoiceMenu();
};

/* Placed in viewport coordinates, so it cannot follow what moves under it. */
const handleInvoiceMenuViewport = () => {
  if (openInvoiceFor.value !== null) closeInvoiceMenu();
};

// Rotate a page in mixed mode
const rotatePage = (pageIndex, degrees) => {
  if (mixedModePages.value[pageIndex]) {
    mixedModePages.value[pageIndex].rotation = (mixedModePages.value[pageIndex].rotation + degrees) % 360;
  }
};

// Close mixed mode modal
const closeMixedModeModal = () => {
  closeInvoiceMenu();
  showMixedModeModal.value = false;
  mixedModePages.value = [];
  processingMixedMode.value = false;
  // Cancelling here ends the whole choice this PDF started, so neither flag
  // nor the pending file survives it.
  processingPdfChoice.value = false;
  pendingPdfInfo.value = { file: null, pageCount: 0 };
  pdfProcessingStatus.value = '';
};

// Confirm mixed mode classification
const confirmMixedModeClassification = async () => {
  processingMixedMode.value = true;

  try {
    const filesToAdd = [];

    // Filter out excluded pages and group by invoice number
    const includedPages = mixedModePages.value.filter(page => page.included);

    // Group pages by invoice number
    const invoiceGroups = {};
    includedPages.forEach((page, index) => {
      const invoiceNum = page.invoiceNumber;
      if (!invoiceGroups[invoiceNum]) {
        invoiceGroups[invoiceNum] = [];
      }
      invoiceGroups[invoiceNum].push(page);
    });

    // Process each invoice group
    for (const [invoiceNum, pages] of Object.entries(invoiceGroups)) {
      if (pages.length === 1) {
        // Single page invoice - apply rotation if needed
        const page = pages[0];

        if (page.rotation && page.rotation !== 0) {
          // Need to rotate the image
          pdfProcessingStatus.value = t('upload.rotatingPage', { invoiceNum });

          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Load the image
          const img = await new Promise((resolve) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.src = page.preview;
          });

          // Calculate canvas size based on rotation
          const isRotated = page.rotation === 90 || page.rotation === 270 || page.rotation === -90 || page.rotation === -270;
          canvas.width = isRotated ? img.height : img.width;
          canvas.height = isRotated ? img.width : img.height;

          // Draw rotated image
          ctx.save();
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate((page.rotation * Math.PI) / 180);
          ctx.drawImage(img, -img.width / 2, -img.height / 2);
          ctx.restore();

          // Convert to blob and file
          const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9));
          const rotatedFile = new File(
            [blob],
            `invoice_${invoiceNum}_rotated.jpg`,
            { type: 'image/jpeg' }
          );
          const preview = URL.createObjectURL(blob);

          filesToAdd.push({
            file: rotatedFile,
            preview,
            fromPdf: true,
            originalPdfName: pendingPdfInfo.value.file.name
          });
        } else {
          // No rotation needed
          filesToAdd.push({
            file: page.file,
            preview: page.preview,
            fromPdf: true,
            originalPdfName: pendingPdfInfo.value.file.name
          });
        }
      } else {
        // Multi-page invoice - combine pages
        pdfProcessingStatus.value = t('upload.combiningPages', { count: pages.length, invoiceNum });

        // Create a combined image from multiple pages
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Load all images
        const images = await Promise.all(
          pages.map(page => {
            return new Promise((resolve) => {
              const img = new Image();
              img.onload = () => resolve({ img, rotation: page.rotation || 0 });
              img.src = page.preview;
            });
          })
        );

        // Calculate combined canvas size considering rotations
        const imageDimensions = images.map(({ img, rotation }) => {
          // Swap width/height for 90 or 270 degree rotations
          const isRotated = rotation === 90 || rotation === 270 || rotation === -90 || rotation === -270;
          return {
            width: isRotated ? img.height : img.width,
            height: isRotated ? img.width : img.height,
            rotation
          };
        });

        const maxWidth = Math.max(...imageDimensions.map(d => d.width));
        const totalHeight = imageDimensions.reduce((sum, d) => sum + d.height, 0);
        const gap = 20; // Gap between pages
        const totalGaps = (images.length - 1) * gap;

        canvas.width = maxWidth;
        canvas.height = totalHeight + totalGaps;

        // Fill with white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw all images with rotation
        let yOffset = 0;
        for (let i = 0; i < images.length; i++) {
          const { img, rotation } = images[i];
          const dim = imageDimensions[i];

          ctx.save();

          // Calculate center position
          const xOffset = (maxWidth - dim.width) / 2;
          const centerX = xOffset + dim.width / 2;
          const centerY = yOffset + dim.height / 2;

          // Move to center, rotate, then draw
          ctx.translate(centerX, centerY);
          ctx.rotate((rotation * Math.PI) / 180);
          ctx.drawImage(img, -img.width / 2, -img.height / 2);

          ctx.restore();

          yOffset += dim.height + gap;
        }

        // Convert canvas to blob and file
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9));
        const combinedFile = new File(
          [blob],
          `invoice_${invoiceNum}_combined.jpg`,
          { type: 'image/jpeg' }
        );
        const preview = URL.createObjectURL(blob);

        filesToAdd.push({
          file: combinedFile,
          preview,
          fromPdf: true,
          originalPdfName: pendingPdfInfo.value.file.name,
          // The user just told us these pages are ONE invoice. Without this the
          // server's "several invoices in one image" split sees a tall stitched
          // image and tears the pages back apart into separate invoices.
          fromMultiPage: true
        });
      }
    }

    // Add all files to upload queue
    scanStore.addFiles(filesToAdd);

    // Close modals
    closeMixedModeModal();
    closeMultiPageModal();

    console.log(`✓ Processed ${Object.keys(invoiceGroups).length} invoices from mixed mode`);
  } catch (error) {
    console.error('Failed to process mixed mode:', error);
    alert(t('upload.processFailed'));
  } finally {
    processingMixedMode.value = false;
    pdfProcessingStatus.value = '';
  }
};

// Close multi-page modal
const closeMultiPageModal = () => {
  showMultiPageModal.value = false;
  pendingPdfInfo.value = { file: null, pageCount: 0 };
  pdfProcessingStatus.value = '';
  processingPdfChoice.value = false;
};

// Handle user's choice for multi-page PDF
const handleMultiPageChoice = async (choice) => {
  const { file, pageCount } = pendingPdfInfo.value;

  if (!file || processingPdfChoice.value) return;

  // Disable buttons to prevent double-clicking
  processingPdfChoice.value = true;

  try {
    const filesToAdd = [];

    if (choice === 'single') {
      // Combine all pages into one image
      pdfProcessingStatus.value = t('upload.combiningFilePages', { count: pageCount, filename: file.name });

      const pages = await extractPdfPages(file, {
        scale: 2.0,
        format: 'jpeg',
        quality: 0.85,
        combinePages: true,
        onProgress: (current, total) => {
          pdfProcessingStatus.value = t('upload.processingPage', { current, total, filename: file.name });
        }
      });

      if (pages.length > 0) {
        const preview = URL.createObjectURL(pages[0].blob);
        filesToAdd.push({
          file: pages[0].file,
          preview,
          fromPdf: true,
          originalPdfName: file.name
        });
        console.log(`✓ Combined ${pageCount} pages from ${file.name} into single invoice`);
      }
    } else if (choice === 'multiple') {
      // Upload each page as separate invoice
      pdfProcessingStatus.value = t('upload.extractingPages', { count: pageCount, filename: file.name });

      const pages = await extractPdfPages(file, {
        scale: 2.0,
        format: 'jpeg',
        quality: 0.85,
        onProgress: (current, total) => {
          pdfProcessingStatus.value = t('upload.processingPage', { current, total, filename: file.name });
        }
      });

      // Add each page as a separate invoice
      for (const pageData of pages) {
        const preview = URL.createObjectURL(pageData.blob);
        filesToAdd.push({
          file: pageData.file,
          preview,
          fromPdf: true,
          originalPdfName: file.name
        });
      }

      console.log(`✓ Split ${file.name} into ${pageCount} separate invoices`);
    } else if (choice === 'mixed') {
      // Mixed mode - let user classify each page
      pdfProcessingStatus.value = t('upload.extractingForClassification', { count: pageCount });

      const pages = await extractPdfPages(file, {
        scale: 2.0,
        format: 'jpeg',
        quality: 0.85,
        onProgress: (current, total) => {
          pdfProcessingStatus.value = t('upload.processingPage', { current, total, filename: file.name });
        }
      });

      // Prepare pages for classification - each starts as separate invoice
      mixedModePages.value = pages.map((pageData, index) => ({
        file: pageData.file,
        preview: URL.createObjectURL(pageData.blob),
        invoiceNumber: index + 1, // Default each page to its own invoice number
        pageIndex: index,
        rotation: 0, // Default rotation
        included: true // Default all pages as included
      }));

      maxInvoiceNumbers.value = pages.length;

      // Close the multi-page modal and open mixed mode modal
      showMultiPageModal.value = false;
      showMixedModeModal.value = true;
      pdfProcessingStatus.value = '';
      // The choice has been answered and the mixed-mode sheet keeps its own
      // flag from here. Leaving this one raised is what left the next PDF's
      // dialogue with all three options disabled.
      processingPdfChoice.value = false;

      return; // Don't add files yet, wait for user classification
    }

    // Add files to upload queue
    if (filesToAdd.length > 0) {
      scanStore.addFiles(filesToAdd);
    }

    // Close modal
    closeMultiPageModal();
  } catch (error) {
    console.error(`Error processing PDF ${file.name}:`, error);
    pdfProcessingStatus.value = '';
    alert(t('upload.pdfProcessFailed', { filename: file.name }));
    closeMultiPageModal();
  }
};

// Watch for prop changes from parent
watch(() => props.selectedCompanyId, (newValue) => {
  selectedCompanyId.value = newValue;
  // Update scan store metadata when company changes
  scanStore.setUploadMetadata({
    company_id: newValue,
    type: selectedType.value,
    is_hard_scan: isHardScanMode.value
  });
});

// Watch for type changes
watch(selectedType, (newValue) => {
  // Update scan store metadata when type changes
  scanStore.setUploadMetadata({
    company_id: selectedCompanyId.value,
    type: newValue,
    is_hard_scan: isHardScanMode.value
  });
});

// Watch for company selector changes
watch(selectedCompanyId, (newValue) => {
  // Emit company change to parent
  emit('company-change', newValue);

  // Update scan store metadata when company changes from selector
  scanStore.setUploadMetadata({
    company_id: newValue,
    type: selectedType.value,
    is_hard_scan: isHardScanMode.value
  });
});

// Watch for hard scan mode changes
watch(isHardScanMode, (newValue) => {
  // Update scan store metadata when hard scan mode changes
  scanStore.setUploadMetadata({
    company_id: selectedCompanyId.value,
    type: selectedType.value,
    is_hard_scan: newValue
  });
});

// Handle paste from clipboard
const handlePaste = async (event) => {
  // Only handle paste if upload form is visible and not processing PDF
  if (!showUploadForm.value || pdfProcessingStatus.value) {
    return;
  }

  const items = event.clipboardData?.items;
  if (!items) return;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    // Check if the item is an image
    if (item.type.indexOf('image') !== -1) {
      event.preventDefault();

      const blob = item.getAsFile();
      if (blob) {
        // Create a File object from the blob with a timestamp name
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const file = new File([blob], `pasted-image-${timestamp}.png`, { type: blob.type });

        // Process the pasted image
        await handleFiles([file]);
      }
      break; // Only handle the first image
    }
  }
};

onMounted(() => {
  loadCompanies();
  // Start polling for status updates
  scanStore.startPolling();

  // Set initial metadata
  if (selectedCompanyId.value || selectedType.value || isHardScanMode.value) {
    scanStore.setUploadMetadata({
      company_id: selectedCompanyId.value,
      type: selectedType.value,
      is_hard_scan: isHardScanMode.value
    });
  }

  // Add paste event listener for clipboard images
  window.addEventListener('paste', handlePaste);

  document.addEventListener('pointerdown', handleInvoiceMenuOutside, true);
  document.addEventListener('keydown', handleInvoiceMenuKey);
  window.addEventListener('scroll', handleInvoiceMenuViewport, true);
  window.addEventListener('resize', handleInvoiceMenuViewport);
});

onUnmounted(() => {
  // Clean up polling when component is destroyed
  scanStore.stopPolling();
  // Remove paste event listener
  window.removeEventListener('paste', handlePaste);

  document.removeEventListener('pointerdown', handleInvoiceMenuOutside, true);
  document.removeEventListener('keydown', handleInvoiceMenuKey);
  window.removeEventListener('scroll', handleInvoiceMenuViewport, true);
  window.removeEventListener('resize', handleInvoiceMenuViewport);
});

const fileInput = ref(null);
const isDragging = ref(false);
/* Open or closed is the user's decision, and it outlives the page.
 *
 * Switching company leaves the dashboard for the companies page and comes
 * back to a fresh one, which used to reopen the form — the state lived only
 * in this component. Nothing but the button should open it, so the choice is
 * remembered and read back here.
 *
 * Wrapped: storage throws in a private window and comes back empty when site
 * data is cleared, and the form being open is the right thing to fall back
 * to.
 */
const UPLOAD_FORM_KEY = 'invoicescan.upload-form-open';

const storedFormState = () => {
  try {
    return localStorage.getItem(UPLOAD_FORM_KEY) !== '0';
  } catch {
    return true;
  }
};

const showUploadForm = ref(storedFormState());
const uploadZoneRef = ref(null);

/* The hide button empties the whole top row, not just this card — the QR
   code and the company's record go with it. They are the page's, so the
   page is told whenever this flips. */
watch(showUploadForm, (visible) => {
  emit('form-visibility', visible);
  try {
    localStorage.setItem(UPLOAD_FORM_KEY, visible ? '1' : '0');
  } catch {
    // Nothing to remember it with; this session keeps the choice anyway.
  }
}, { immediate: true });

const uploadQueue = computed(() => {
  const queue = scanStore.uploadQueue || [];
  // Filter queue by selected company if one is selected
  if (props.selectedCompanyId) {
    return queue.filter(item => {
      // Show items that match the selected company
      return item.company_id === props.selectedCompanyId;
    });
  }
  return queue;
});

const hasCompleted = computed(() => {
  return uploadQueue.value.some(item => item.status === 'completed');
});

const completedScans = computed(() => {
  // Return all completed scans with data
  return uploadQueue.value.filter(item =>
    item.status === 'completed' && item.scanData
  );
});

// Watch for completed scans and emit event
let lastCompletedCount = 0;
watch(
  () => uploadQueue.value.filter(item => item.status === 'completed').length,
  (currentCompletedCount) => {
    if (currentCompletedCount > lastCompletedCount) {
      console.log(`✅ Scan completed! Emitting refresh event. (${currentCompletedCount} total completed)`);
      emit('scan-completed');
      
      // Check newly completed scans for company validation
      const completedScans = uploadQueue.value.filter(item => item.status === 'completed');
      const newlyCompletedScans = completedScans.slice(0, currentCompletedCount - lastCompletedCount);
      
      newlyCompletedScans.forEach(scan => {
        if (scan.scanData) {
          checkCompanyValidation(scan, scan.scanData);
        }
      });
      
      lastCompletedCount = currentCompletedCount;
    }
  },
  { immediate: false }
);

// --- Invoice register (multi-page) upload ---
const registerInput = ref(null);
const registerUploading = ref(false);
const registerStatus = ref('');
// The register button is only shown when the user enabled the feature in settings.
const registerEnabled = computed(() => authStore.gasStationMultiInvoice);

const triggerRegisterInput = () => {
  // The payment method is now read from each register row (the fiscal-receipt
  // line), so we open the file picker directly — no register-wide choice needed.
  registerInput.value?.click();
};

const handleRegisterSelect = async (event) => {
  const files = Array.from(event.target.files || []);
  event.target.value = ''; // allow re-selecting the same files
  if (files.length === 0) return;

  registerUploading.value = true;

  try {
    // A register may be uploaded as several page images or as a single PDF with
    // all pages. A whole-register PDF can be tens of MB (e.g. 31 phone photos),
    // which the server rejects with 413, so we rasterise the PDF IN THE BROWSER
    // and upload compressed page images instead. (The server still accepts a raw
    // PDF on this endpoint, e.g. from mobile, and renders it itself.)
    const pageFiles = [];
    for (const f of files) {
      if (isPDF(f)) {
        registerStatus.value = 'Разчитане на PDF страници...';
        // High render scale so photos embedded at ~3000px are captured near their
        // native resolution (dense number columns need the detail).
        const pages = await extractPdfPages(f, { scale: 5, format: 'jpeg', quality: 0.92 });
        for (const p of pages) pageFiles.push(p.file);
      } else {
        pageFiles.push(f);
      }
    }

    if (pageFiles.length === 0) {
      alert('Файлът не съдържа страници.');
      return;
    }

    // Upload in small batches so each request stays under the server's
    // post_max_size — a whole register can be 20+ MB even after compression,
    // which would 413. The first request starts the register and returns its id;
    // later requests pass it back; the last one sets final=1 to begin processing.
    // Smaller batches because register pages are now kept at a higher resolution
    // (~2600px, larger files) — keeps each request under post_max_size.
    const BATCH_SIZE = 3;
    const total = pageFiles.length;
    let registerId = null;

    for (let i = 0; i < total; i += BATCH_SIZE) {
      const chunk = pageFiles.slice(i, i + BATCH_SIZE);
      const formData = new FormData();
      for (const f of chunk) {
        // Keep register pages at ~2600px for legible number columns.
        const compressed = await resizeImage(f, 2, 2600);
        formData.append('files[]', compressed);
      }
      if (registerId) {
        formData.append('register_id', registerId);
      } else if (selectedCompanyId.value) {
        formData.append('company_id', selectedCompanyId.value);
      }
      if (i + BATCH_SIZE >= total) {
        formData.append('final', '1');
      }

      registerStatus.value = `Качване ${Math.min(i + BATCH_SIZE, total)}/${total} страници...`;

      const { data } = await api.post('/api/invoices/register', formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'x-requested-with': 'XMLHttpRequest' },
      });
      registerId = data.register_id ?? data.job_id ?? registerId;
    }

    const jobId = registerId;
    registerStatus.value = 'Обработка на регистъра (може да отнеме 1-2 минути)...';

    // Poll the container invoice until processing finishes.
    let done = false;
    let completedSummary = null; // handed up so Home can show a persistent notice
    for (let i = 0; i < 200 && !done; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const res = await api.get(`/api/invoices/${jobId}`);
        const status = res.data?.status || res.data?.invoice?.status;
        // The register container ends as 'archived' (hidden from the list).
        if (status === 'completed' || status === 'archived' || status === 'failed') {
          done = true;
          const count = res.data?.result?.child_count
            ?? res.data?.invoice?.result?.child_count
            ?? 0;
          const summary = res.data?.result?.summary
            ?? res.data?.invoice?.result?.summary
            ?? null;
          completedSummary = summary;
          if (status === 'failed') {
            alert('Обработката на регистъра се провали.');
          } else {
            let msg = `Готово: създадени ${count} фактури от регистъра.`;

            const missing = Array.isArray(summary?.missing_numbers) ? summary.missing_numbers : [];
            const missingCount = summary?.missing_count ?? missing.length;
            const sumMismatch = summary && summary.expected_with_vat > 0 && !summary.matches;

            // Explicit indication when invoices are missing: the register's № run
            // consecutively, so any hole in the sequence is a missing invoice — list
            // the exact numbers so the user knows precisely what to check.
            if (missingCount > 0) {
              const shown = missing.slice(0, 25).join(', ');
              const more = missing.length > 25 ? ` … (+${missing.length - 25} още)` : '';
              msg += `\n\n⚠️ Липсват ${missingCount} фактури (пропуски в номерата):\n${shown}${more}`
                   + `\n\nПроверете тези номера в регистъра и при нужда пуснете пак.`;
            } else if (sumMismatch) {
              // No sequence gap, but the total still disagrees with the printed
              // "Сумарно" — a duplicate or an amount was mis-read.
              const diff = (summary.extracted_with_vat - summary.expected_with_vat).toFixed(2);
              msg += `\n\n⚠️ Обща сума с ДДС: получена ${summary.extracted_with_vat}, очаквана ${summary.expected_with_vat} (по „Сумарно“).`
                   + `\nРазлика: ${diff} — възможно е да се дублират редове или сума да е разчетена грешно. Проверете.`;
            } else if (summary) {
              // Everything reconciles — reassure the user nothing is missing.
              msg += `\n\n✅ Всички фактури са налични (номерата са последователни${summary.expected_with_vat > 0 ? ' и сумата съвпада с „Сумарно“' : ''}).`;
            }

            alert(msg);
          }
        }
      } catch (e) {
        // keep polling on transient errors
      }
    }

    emit('scan-completed');
    emit('register-completed', completedSummary);
  } catch (error) {
    console.error('Register upload failed:', error);
    alert(error?.response?.data?.message || 'Качването на регистъра се провали.');
  } finally {
    registerUploading.value = false;
    registerStatus.value = '';
  }
};

const triggerFileInput = () => {
  fileInput.value?.click();
};

const openCamera = () => {
  // Trigger camera input
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.capture = 'environment'; // Use rear camera on mobile
  input.onchange = (e) => {
    const files = Array.from(e.target.files || []);
    handleFiles(files);
  };
  input.click();
};

// The multi-page PHOTO flow relies on the phone's rear camera
// (capture="environment"), so its entry point is only offered on an actual
// mobile device. The same question is asked on the dashboard, so the answer
// lives in one place — see utils/device.js.

// Mobile only: ask which kind of document is being photographed before opening
// the camera. Desktop keeps its existing behaviour untouched.
const showMobileDocChooser = ref(false);

const chooseDocType = (type) => {
  showMobileDocChooser.value = false;
  if (type === 'multi') {
    captureMultiPagePhoto();
  } else {
    openCamera();
  }
};

/**
 * Photograph ONE page of a multi-page invoice. Each shot is staged in
 * multiPageFiles; confirmMultiPageUpload() then stitches them into a single
 * image so the whole invoice is scanned as ONE document (not N invoices).
 */
const captureMultiPagePhoto = () => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.capture = 'environment';
  input.onchange = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      multiPageFiles.value.push({ file, preview: URL.createObjectURL(file) });
    });
    if (multiPageFiles.value.length > 0) {
      isMultiPageMode.value = true;
    }
  };
  input.click();
};

const handleFileSelect = (event) => {
  const files = Array.from(event.target.files || []);
  handleFiles(files);
  // Reset input so same file can be selected again
  event.target.value = '';
};

const handleDrop = (event) => {
  isDragging.value = false;
  const files = Array.from(event.dataTransfer.files);
  handleFiles(files);
};

const resizeImage = async (file, maxSizeMB = 4) => {
  // If not an image, return original file
  if (!file.type.startsWith('image/')) {
    return file;
  }

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Create canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Calculate new dimensions maintaining aspect ratio
        let width = img.width;
        let height = img.height;

        // More aggressive size reduction for very large images
        const originalSizeMB = file.size / (1024 * 1024);
        let maxDimension = 2048; // Default max dimension

        // Adjust max dimension based on original file size
        if (originalSizeMB > 20) {
          maxDimension = 1600; // Very aggressive for huge files
        } else if (originalSizeMB > 10) {
          maxDimension = 1920; // Aggressive for large files
        } else if (originalSizeMB > 5) {
          maxDimension = 2048; // Moderate for medium files
        }

        // Always resize if either dimension exceeds max
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height / width) * maxDimension);
            width = maxDimension;
          } else {
            width = Math.round((width / height) * maxDimension);
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw image on canvas with better quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob with quality adjustment
        let quality = 0.85;

        // Start with lower quality for very large original files
        if (originalSizeMB > 15) {
          quality = 0.7;
        } else if (originalSizeMB > 8) {
          quality = 0.75;
        }

        const tryCompress = (q) => {
          canvas.toBlob(
            (blob) => {
              const compressedSizeMB = blob.size / (1024 * 1024);

              // If still too large and quality can be reduced further, try again
              if (compressedSizeMB > maxSizeMB && q > 0.4) {
                tryCompress(q - 0.1);
              } else if (compressedSizeMB > maxSizeMB) {
                // Last resort: reduce dimensions further
                canvas.width = Math.round(canvas.width * 0.8);
                canvas.height = Math.round(canvas.height * 0.8);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                tryCompress(0.7);
              } else {
                // Create new file from blob
                const compressedFile = new File([blob], file.name.replace(/\.\w+$/, '.jpg'), {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                console.log(`✓ Compressed ${file.name}: ${originalSizeMB.toFixed(2)}MB → ${compressedSizeMB.toFixed(2)}MB (${width}×${height}, Q:${Math.round(q*100)}%)`);
                resolve(compressedFile);
              }
            },
            'image/jpeg',
            q
          );
        };

        tryCompress(quality);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

const onMultiPageModeChange = () => {
  if (!isMultiPageMode.value) {
    // Clear staging when turning off multi-page mode
    multiPageFiles.value.forEach(item => {
      if (item.preview) URL.revokeObjectURL(item.preview);
    });
    multiPageFiles.value = [];
  }
};

const removePage = (index) => {
  const item = multiPageFiles.value[index];
  if (item.preview) URL.revokeObjectURL(item.preview);
  multiPageFiles.value.splice(index, 1);
};

const clearMultiPageFiles = () => {
  multiPageFiles.value.forEach(item => {
    if (item.preview) URL.revokeObjectURL(item.preview);
  });
  multiPageFiles.value = [];
};

const cancelMultiPage = () => {
  clearMultiPageFiles();
  isMultiPageMode.value = false;
};

const combineImagesVertically = async (imageFiles) => {
  return new Promise((resolve, reject) => {
    const images = [];
    let loadedCount = 0;

    // Load all images
    imageFiles.forEach((item, index) => {
      const img = new Image();
      img.onload = () => {
        images[index] = img;
        loadedCount++;

        if (loadedCount === imageFiles.length) {
          // All images loaded, combine them
          try {
            // Calculate total height and max width
            let totalHeight = 0;
            let maxWidth = 0;

            images.forEach(img => {
              totalHeight += img.height;
              maxWidth = Math.max(maxWidth, img.width);
            });

            // Create canvas
            const canvas = document.createElement('canvas');
            canvas.width = maxWidth;
            canvas.height = totalHeight;
            const ctx = canvas.getContext('2d');

            // Fill with white background
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, maxWidth, totalHeight);

            // Draw each image
            let currentY = 0;
            images.forEach(img => {
              // Center image horizontally if narrower than canvas
              const x = (maxWidth - img.width) / 2;
              ctx.drawImage(img, x, currentY);
              currentY += img.height;
            });

            // Convert canvas to blob
            canvas.toBlob((blob) => {
              if (blob) {
                // Create file from blob
                const timestamp = new Date().getTime();
                const fileName = `multi_page_invoice_${timestamp}.jpg`;
                const file = new File([blob], fileName, { type: 'image/jpeg' });
                resolve(file);
              } else {
                reject(new Error('Failed to create image blob'));
              }
            }, 'image/jpeg', 0.9);
          } catch (error) {
            reject(error);
          }
        }
      };

      img.onerror = () => {
        reject(new Error(`Failed to load image ${index + 1}`));
      };

      img.src = item.preview;
    });
  });
};

const confirmMultiPageUpload = async () => {
  if (multiPageFiles.value.length === 0) return;

  processingMultiPage.value = true;
  pdfProcessingStatus.value = `Combining ${multiPageFiles.value.length} pages...`;

  try {
    // Combine all images into one
    const combinedFile = await combineImagesVertically(multiPageFiles.value);

    // Create preview
    const preview = URL.createObjectURL(combinedFile);

    pdfProcessingStatus.value = '';

    // Add to upload queue
    scanStore.addFiles([{ file: combinedFile, preview, fromMultiPage: true }]);

    // Clear staging
    clearMultiPageFiles();
    isMultiPageMode.value = false;
    processingMultiPage.value = false;
  } catch (error) {
    console.error('Error combining images:', error);
    pdfProcessingStatus.value = '';
    processingMultiPage.value = false;
    alert('Failed to combine pages. Please try again.');
  }
};

const handleFiles = async (files) => {
  const allFilesToProcess = [];

  // Process each file
  for (const file of files) {
    if (isPDF(file)) {
      // Check if PDF has multiple pages
      try {
        pdfProcessingStatus.value = `Analyzing ${file.name}...`;

        // First, check if this is a Bulgarian Gas Station invoice
        const bulgarianData = await processBulgarianGasStationPdf(file);

        if (bulgarianData && bulgarianData.companies.length > 0) {
          // This is a Bulgarian Gas Station invoice with structured data
          console.log('🚗 Bulgarian Gas Station invoice detected!');

          // Store pending data and show modal
          pendingBulgarianData.value = bulgarianData;
          pendingBulgarianFile.value = file;
          showBulgarianModal.value = true;

          pdfProcessingStatus.value = '';
          // Don't process further files while modal is open
          return;
        }

        // Not a Bulgarian Gas Station invoice, continue with normal processing
        const pageCount = await getPdfPageCount(file);

        if (pageCount > 1) {
          // Multi-page PDF - show modal for user to choose
          pendingPdfInfo.value = { file, pageCount };
          // A fresh question is always answerable: whatever an earlier PDF
          // left behind must not arrive as three disabled options.
          processingPdfChoice.value = false;
          showMultiPageModal.value = true;
          pdfProcessingStatus.value = '';
          // Don't process further files while modal is open
          return;
        } else {
          // Single page PDF - convert to image
          pdfProcessingStatus.value = `Converting ${file.name} to image...`;

          const pages = await extractPdfPages(file, {
            scale: 2.0,
            format: 'jpeg',
            quality: 0.85
          });

          if (pages.length > 0) {
            const preview = URL.createObjectURL(pages[0].blob);
            allFilesToProcess.push({
              file: pages[0].file,
              preview,
              fromPdf: true,
              originalPdfName: file.name
            });
          }

          console.log(`✓ Converted single-page PDF ${file.name} to image`);
        }
      } catch (error) {
        console.error(`Error processing PDF ${file.name}:`, error);
        // Fallback: add original PDF file
        allFilesToProcess.push({ file, preview: null });
      }
    } else {
      // Not a PDF - process as image with resize
      const processedFile = await resizeImage(file);

      let preview = null;
      if (processedFile.type.startsWith('image/')) {
        preview = URL.createObjectURL(processedFile);
      }
      allFilesToProcess.push({ file: processedFile, preview });
    }
  }

  pdfProcessingStatus.value = '';

  // Add all processed files to queue
  if (allFilesToProcess.length > 0) {
    scanStore.addFiles(allFilesToProcess);
  }
};

// Create Bulgarian Gas Station invoices via API
const createBulgarianGasStationInvoices = async (invoices, originalFileName) => {
  try {
    console.log(`📤 Creating ${invoices.length} Bulgarian Gas Station invoices...`);

    // Show progress bar
    bulgarianInvoiceProgress.value = {
      show: true,
      current: 0,
      total: invoices.length,
      fileName: originalFileName
    };

    // Create each invoice using the manual invoice API
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < invoices.length; i++) {
      const invoiceData = invoices[i];
      try {
        const response = await api.post('/api/invoices/manual', {
          result: invoiceData
        });

        if (response.data && response.data.invoice) {
          // Add successful invoice to scan store
          const newScan = {
            id: response.data.invoice.id,
            filename: `${originalFileName} - Invoice ${invoiceData.invoice_number}`,
            status: 'completed',
            progress: 100,
            result: invoiceData,
            scanData: invoiceData,
            scanId: response.data.invoice.id,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };

          // Add to scans list
          scanStore.scans.unshift(newScan);
          
          // Validate company on invoice
          checkCompanyValidation(newScan, invoiceData);
          
          successCount++;

          console.log(`✅ Created invoice ${invoiceData.invoice_number}`);
        }
      } catch (error) {
        console.error(`❌ Failed to create invoice ${invoiceData.invoice_number}:`, error);
        failCount++;
      }

      // Update progress
      bulgarianInvoiceProgress.value.current = i + 1;
    }

    // Hide progress bar
    bulgarianInvoiceProgress.value.show = false;

    // Show success modal
    bulgarianSuccessData.value = {
      successCount,
      failCount,
      fileName: originalFileName
    };
    showBulgarianSuccessModal.value = true;

    console.log(`📊 Bulgarian Gas Station invoice creation complete: ${successCount} success, ${failCount} failed`);
    
    // Refresh subscription data to update scan count
    console.log('🔄 Refreshing subscription after Bulgarian bulk upload...');
    await authStore.fetchUser();
    console.log('✅ Subscription refreshed. New count:', authStore.subscription?.scans_used);
  } catch (error) {
    console.error('Error creating Bulgarian Gas Station invoices:', error);
    bulgarianInvoiceProgress.value.show = false;
    alert('Failed to create invoices. Please try again.');
  }
};

const closeBulgarianSuccessModal = () => {
  showBulgarianSuccessModal.value = false;
};

// Handle Bulgarian modal cancel
const handleBulgarianCancel = () => {
  showBulgarianModal.value = false;
  pendingBulgarianData.value = null;
  pendingBulgarianFile.value = null;
  processingBulgarian.value = false;
};

// Handle Bulgarian modal confirm with selected role
const handleBulgarianConfirm = async (data) => {
  try {
    processingBulgarian.value = true;

    // Get selected company data
    const selectedCompany = companies.value.find(c => c.id === selectedCompanyId.value);

    // Map role to type: buyer = expense (we're buying), seller = income (we're selling)
    const invoiceType = data.role === 'seller' ? 'income' : 'expense';

    // Convert to invoice format with company VAT data and role
    const invoices = convertToInvoiceFormat(pendingBulgarianData.value, {
      company_id: selectedCompanyId.value,
      type: invoiceType,
      invoiceRole: data.role,
      paymentMethod: data.paymentMethod,
      companyData: selectedCompany ? {
        vat_number: selectedCompany.vat_number || selectedCompany.registration_number || '',
        tax_id: selectedCompany.registration_number || '',
        name: selectedCompany.name,
        address: selectedCompany.address,
        phone: selectedCompany.phone,
        email: selectedCompany.email
      } : null
    });

    // Debug: log the type for each invoice
    console.log(`📋 Creating ${invoices.length} invoices with type: ${invoiceType} (role: ${data.role})`);
    invoices.forEach(inv => {
      console.log(`  Invoice ${inv.invoice_number}: type="${inv.type}"`);
    });

    // Close modal
    showBulgarianModal.value = false;

    // Create invoices directly via API
    await createBulgarianGasStationInvoices(invoices, pendingBulgarianFile.value.name);

    // Clear pending data
    pendingBulgarianData.value = null;
    pendingBulgarianFile.value = null;

  } catch (error) {
    console.error('Error processing Bulgarian invoices:', error);
    alert('Failed to process invoices. Please try again.');
  } finally {
    processingBulgarian.value = false;
  }
};

const retryUpload = (uploadId) => {
  scanStore.retryUpload(uploadId);
};

const cancelUpload = (uploadId) => {
  scanStore.cancelUpload(uploadId);
};

const clearCompleted = () => {
  const completedIds = uploadQueue.value
    .filter(item => item.status === 'completed')
    .map(item => item.id);

  completedIds.forEach(id => scanStore.removeFromQueue(id));
};

const viewScan = (scanId) => {
  router.push(`/scans/${scanId}`);
};

const viewInvoice = (scanId) => {
  router.push(`/scans/${scanId}`);
};

const showOriginal = (scanId) => {
  // Open original file in new tab
  window.open(`/api/invoices/${scanId}/original`, '_blank');
};

const handleThumbnailError = (event) => {
  // Hide broken image, show placeholder instead
  event.target.style.display = 'none';
};

const scanAnother = () => {
  // Clear completed items
  clearCompleted();
  // Show upload form
  showUploadForm.value = true;
  // Scroll to upload zone
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const removeCompletedItem = (itemId) => {
  scanStore.removeFromQueue(itemId);
};

const handleRescan = async (useHardScan) => {
  const result = await performRescan(useHardScan);
  if (result) {
    // Refresh the scan data
    router.go(0); // Reload page to show new scan results
  }
};

// Validate if invoice contains the selected company
const checkCompanyValidation = (invoice, invoiceData) => {
  console.log('🔍 checkCompanyValidation called', {
    hasSelectedCompany: !!selectedCompanyId.value,
    selectedCompanyId: selectedCompanyId.value
  });
  
  if (!selectedCompanyId.value) return;
  
  // Find the selected company
  const company = companies.value.find(c => c.id === selectedCompanyId.value);
  
  console.log('🔍 Company found:', {
    hasCompany: !!company,
    companyName: company?.name,
    validateEnabled: authStore.validateCompanyOnScan,
    fullCompany: company
  });
  
  if (!company || !authStore.validateCompanyOnScan) return;
  
  console.log('🔍 Invoice data:', {
    buyerName: invoiceData?.buyer_name,
    buyerVat: invoiceData?.buyer_vat,
    sellerName: invoiceData?.seller_name,
    sellerVat: invoiceData?.seller_vat,
    invoiceNumber: invoiceData?.invoice_number
  });
  
  // Validate invoice against company
  const validation = validateCompanyOnInvoice({ result: invoiceData }, company, { enabled: true });
  
  console.log('🔍 Validation result:', validation);
  
  if (!validation.isValid) {
    // Log validation failure - warning will appear in the banner after scans refresh
    const invoiceNum = validation.invoiceNumber || t('upload.invoice');
    const companyName = validation.companyName || company.name;
    
    console.warn(`⚠️ Invoice ${invoiceNum} does not contain company "${companyName}"`);
    console.warn('Missing:', validation.missing);
  }
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

// Expose methods to parent component
/**
 * Open the upload form from the dashboard's empty state.
 *
 * When the form is already on screen the file picker opens straight away —
 * that is what 'start scanning' means, and scrolling alone left the person
 * looking at a form that had not done anything.
 *
 * When the form is collapsed it is revealed and scrolled to instead, without
 * the picker: the input does not exist until the next tick, and a browser
 * refuses a file dialog opened that long after the click.
 */
const revealUploadForm = () => {
  if (showUploadForm.value) {
    triggerFileInput();
    return;
  }

  showUploadForm.value = true;
  nextTick(() => {
    // 'start', not 'center': centring a zone near the top of the page put the
    // button above it in the middle of the view instead.
    uploadZoneRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
};

defineExpose({
  triggerFileInput,
  revealUploadForm
});
</script>

<style scoped>
.upload-queue {
  max-width: 600px;
  margin: 0 auto;
}

/* Toggle Upload Form Buttons */
.btn-toggle-form,
.btn-hide-form {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 20px;
  height: 45px;
  border: 2px solid var(--primary);
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 20px;
  background: white;
  color: var(--primary);
}

.btn-toggle-form:hover,
.btn-hide-form:hover {
  background: var(--primary-light);
  transform: translateY(-1px);
}

/* Its twin's dress: the button that hides the form and the one that brings
   it back are the same action in two states, so they are drawn alike —
   hairline border, no fill, blue text, a tint and a blue edge on hover. It
   keeps the full width, because that is its place in this column. */
.btn-hide-form {
  gap: 6px;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--primary);
  font-size: 12px;
}

.btn-hide-form:hover {
  background: var(--primary-light);
  border-color: var(--primary);
  transform: none;
}

/* "Show the upload form", dressed like the swap-company button beside it.
 *
 * The two stand next to each other on the collapsed row and are the same
 * kind of thing — a quiet action on a white bar — so they are drawn the same
 * way: a hairline border, no fill, blue text, and a tint with a blue edge on
 * hover. It was a 2px blue frame at 15px type, which read as the page's main
 * button while sitting beside a secondary one. */
.btn-toggle-form {
  width: auto;
  gap: 6px;
  padding: 0 14px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 12px;
  background: transparent;
  color: var(--primary);
}

.btn-toggle-form:hover {
  background: var(--primary-light);
  border-color: var(--primary);
  transform: none;
}

.pdf-processing-banner {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 16px 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 15px;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  animation: slideDown 0.3s ease;
}

/* Bulgarian Invoice Progress Overlay */
.bulgarian-progress-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
}

.bulgarian-progress-card {
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: scaleIn 0.3s ease;
}

.progress-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.progress-icon {
  font-size: 32px;
  color: #3b82f6;
}

.progress-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
}

.progress-info {
  margin-bottom: 20px;
}

.progress-filename {
  font-size: 14px;
  color: #4b5563;
  margin: 0 0 8px 0;
  font-weight: 500;
}

.progress-count {
  font-size: 16px;
  color: #1e293b;
  margin: 0;
  font-weight: 600;
}

.progress-bar-container {
  width: 100%;
  height: 12px;
  background: #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 12px;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 6px;
  transition: width 0.3s ease;
}

.progress-percentage {
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  color: #3b82f6;
}

/* Bulgarian Success Modal */
.bulgarian-success-modal {
  max-width: 500px;
}

.bulgarian-success-modal .modal-header {
  text-align: center;
  padding-bottom: 24px;
}

.success-icon-wrapper {
  margin-bottom: 16px;
}

.success-icon {
  font-size: 64px;
  color: #22c55e;
  animation: successPulse 0.6s ease;
}

@keyframes successPulse {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.bulgarian-success-modal .modal-header h3 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
}

.success-summary {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 8px;
}

.summary-label {
  font-size: 14px;
  color: #4b5563;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-value {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.success-row {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.success-row .summary-label {
  color: #16a34a;
}

.success-value {
  color: #16a34a;
  font-size: 20px;
}

.error-row {
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.error-row .summary-label {
  color: #dc2626;
}

.error-value {
  color: #dc2626;
  font-size: 20px;
}

.btn-confirm-success {
  background: #22c55e;
  color: white;
  border: none;
  padding: 12px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  width: 100%;
  justify-content: center;
}

.btn-confirm-success:hover {
  background: #16a34a;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.form-select {
  padding: 12px 16px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.form-select:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--primary-light);
}

.form-select:hover {
  border-color: var(--primary);
}

/* Multi-Page Mode */
.multi-page-option {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  user-select: none;
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.checkbox-text {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
}

.checkbox-text svg {
  color: var(--primary);
}

.checkbox-description {
  margin: 8px 0 0 32px;
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.5;
}

/* Hard Scan Option */
.hard-scan-option {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 2px solid #f59e0b;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;
}

.hard-scan-option .checkbox-text svg {
  color: #f59e0b;
}

.hard-scan-option .checkbox-label input[type="checkbox"]:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.hard-scan-option .checkbox-label:has(input:disabled) {
  cursor: not-allowed;
  opacity: 0.7;
}

.upload-zone {
  background: white;
  border: 2px solid var(--primary);
  /* 12px is the page's card corner — the cards beside this one, the filter
     card, the table and the totals all use it. */
  border-radius: 12px;
  padding: 60px 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 20px;
}

.upload-zone.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.upload-zone:hover:not(.disabled) {
  border-color: var(--primary-hover);
  background: var(--primary-light);
}

.upload-zone.drag-over:not(.disabled) {
  border-color: var(--primary-hover);
  background: var(--blue-light);
  transform: scale(1.01);
}

.upload-zone-content {
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.upload-icon-glyph {
  color: #4b5563;
}

.upload-icon {
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}

.upload-text {
  font-size: 17px;
  color: var(--text-primary);
  margin: 0 0 8px 0;
  font-weight: 500;
}

.upload-subtext {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

/* Limit Reached Message */
.limit-reached-message {
  background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  border: 2px solid #f59e0b;
  border-radius: 16px;
  padding: 48px 40px;
  text-align: center;
  margin-bottom: 20px;
}

.limit-title {
  font-size: 24px;
  font-weight: 700;
  color: #92400e;
  margin: 0 0 12px 0;
}

.limit-description {
  font-size: 16px;
  color: #78350f;
  margin: 0 0 20px 0;
  line-height: 1.5;
}

.limit-details {
  background: white;
  border-radius: 12px;
  padding: 16px 24px;
  margin: 0 auto 28px;
  max-width: 300px;
}

.limit-details p {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #92400e;
}

.btn-upgrade {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 32px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.btn-upgrade:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(245, 158, 11, 0.4);
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
}

.btn-upgrade:active {
  transform: translateY(0);
}

.hidden {
  display: none;
}

.upload-buttons {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 32px;
}

/* Mobile document-type chooser */
.doc-chooser-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh;           /* track the dynamic mobile viewport (address bar) */
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;      /* centre the dialog on screen */
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
  z-index: 2000;
}

.doc-chooser {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 16px;
  padding: 18px 16px 10px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.28);
}

.doc-chooser-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  margin: 4px 0 12px;
  text-align: center;
}

.doc-choice {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px;
  margin-bottom: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
}

.doc-choice:active {
  background: #eef2f7;
}

.doc-choice-icon {
  font-size: 20px;
  color: #3b82f6;
  flex-shrink: 0;
}

.doc-choice-text {
  display: flex;
  flex-direction: column;
}

.doc-choice-name {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}

.doc-choice-desc {
  font-size: 12px;
  color: #4b5563;
}

.doc-chooser-cancel {
  width: 100%;
  padding: 12px;
  background: transparent;
  border: none;
  color: #4b5563;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

/* Multi-page photo staging (mobile only) */
.multipage-panel {
  margin: 12px 0;
  padding: 14px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.multipage-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.multipage-title {
  font-size: 13px;
  font-weight: 700;
  color: #334155;
}

.multipage-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  color: #0f172a;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
}

.multipage-close:hover {
  background: #f8fafc;
  color: #dc2626;
  border-color: #cbd5e1;
}

.multipage-close svg {
  width: 13px;
  height: 13px;
  display: block;
}

.multipage-pages {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 2px 2px 8px;
}

.multipage-page {
  position: relative;
  flex: 0 0 auto;
  width: 76px;
  height: 100px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.multipage-page img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 8px;
}

.multipage-page-num {
  position: absolute;
  left: 5px;
  bottom: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  background: rgba(15, 23, 42, 0.8);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  border-radius: 5px;
  padding: 0 5px;
}

.multipage-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  color: #0f172a;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
}

.multipage-remove:hover {
  background: #f8fafc;
  color: #dc2626;
  border-color: #cbd5e1;
}

.multipage-remove svg {
  width: 11px;
  height: 11px;
  display: block;
}

.multipage-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.multipage-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 40px;
  padding: 0 12px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;
}

.multipage-btn svg {
  width: 13px;
  height: 13px;
  display: block;
}

.multipage-btn-add {
  background: #0ea5e9;
  color: #fff;
}

.multipage-btn-add:hover:not(:disabled) {
  background: #0284c7;
  transform: translateY(-1px);
}

.multipage-btn-done {
  background: #10b981;
  color: #fff;
}

.multipage-btn-done:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
}

.multipage-btn:disabled {
  opacity: 0.6;
  cursor: default;
  transform: none;
}

.btn-camera,
.btn-upload {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 28px;
  height: 45px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 160px;
}

.btn-camera {
  background: var(--primary);
  color: white;
  box-shadow: 0 2px 6px rgba(14, 165, 233, 0.3);
}

.btn-camera:hover {
  background: var(--primary-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.4);
}

.btn-upload {
  background: white;
  color: var(--text-primary);
  border: 1.5px solid var(--border);
}

.btn-upload:hover {
  background: var(--bg-gray);
  border-color: var(--gray-light);
  transform: translateY(-1px);
}

/* Uploading a file is the main thing asked of this form on a desktop, where
   the camera button is not there at all, so it is the one in colour. The
   register beside it keeps the quiet treatment. */
.btn-upload--primary {
  background: var(--primary);
  color: white;
  border: none;
  box-shadow: 0 2px 6px rgba(14, 165, 233, 0.3);
}

.btn-upload--primary:hover {
  background: var(--primary-hover);
  border: none;
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.4);
}

/* The upload queue, at the dashboard's scale.
 *
 * It was five 140px cards — a thumbnail the size of a stamp album, the name
 * on one line, the size on the next, the state on a third — which on five
 * files made the middle column taller than the screen and pulled the three
 * cards beside it out of shape. It is a list of rows now: one line each, the
 * state read from the bar under it. */
.file-queue {
  background: white;
  border: 1px solid #dde3ea;
  border-radius: 12px;
  padding: 0;
  box-shadow: none;
}

.queue-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin: 0;
  padding: 9px 12px;
  border-bottom: 1px solid #dde3ea;
}

.queue-header h3 {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
}

.btn-clear {
  background: none;
  border: none;
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 6px;
  transition: all 0.15s ease;
}

.btn-clear:hover {
  background: transparent;
  color: var(--primary);
  transform: translateY(-2px);
}

.file-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 4px;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 8px;
  background: transparent;
  border-radius: 6px;
  border: none;
  transition: background 0.15s ease;
}

.file-item + .file-item {
  border-top: 1px solid var(--gray-lighter, #f1f5f9);
}

.file-item:hover {
  background: #fbfcfd;
}

/* Only a failure is marked on the edge.
 *
 * Every state had its own coloured bar, which put a stripe beside each row
 * saying what the row already says in words: the spinner and its text while
 * it works, the tick when it is done. The one thing worth catching the eye
 * from across the page is a file that did not go through. */
.file-item.failed {
  box-shadow: inset 2px 0 0 0 var(--danger);
}

.file-preview {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 4px;
  overflow: hidden;
  background: var(--bg-gray);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dde3ea;
}

.file-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.file-icon {
  color: var(--text-light);
}

.file-info {
  flex: 1;
  min-width: 0;
}

/* Name on the left, size on the right of the same line. */
.file-head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.file-name {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-size {
  flex-shrink: 0;
  font-size: 11px;
  color: var(--text-light, #64748b);
  white-space: nowrap;
}

.progress-bar {
  height: 3px;
  background: var(--gray-lighter, #f1f5f9);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 3px;
  position: relative;
}

.progress-fill {
  height: 100%;
  background: var(--primary);
  border-radius: 2px;
  transition: width 0.3s ease;
  position: relative;
  overflow: hidden;
}

.progress-fill.processing {
  background: var(--warning);
}

.scanning-line {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg,
    transparent 0%,
    transparent 40%,
    rgba(255, 255, 255, 0.9) 50%,
    transparent 60%,
    transparent 100%
  );
  animation: scan 2s ease-in-out infinite;
}

@keyframes scan {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.upload-state {
  margin-top: 2px;
}

.processing-state {
  margin-top: 2px;
}

.processing-text {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--warning);
  font-size: 10px;
  font-weight: 600;
  margin-bottom: 0;
}

.loading-spinner {
  display: inline-block;
  width: 9px;
  height: 9px;
  border: 2px solid var(--warning-light);
  border-top-color: var(--warning);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.status-success {
  color: var(--success);
  font-size: 13px;
  font-weight: 500;
  margin-top: 8px;
}

.status-error {
  color: var(--danger);
  font-size: 10px;
  font-weight: 600;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-actions {
  display: flex;
  gap: 2px;
  align-items: center;
  flex-shrink: 0;
}

/* The marks at the end of a row, drawn like the dashboard's: nothing behind
   them, and the hover is the mark turning blue and lifting two pixels. */
.btn-queue-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  min-width: 22px;
  padding: 0;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.btn-queue-action:hover {
  background: transparent;
  color: var(--primary);
  transform: translateY(-2px);
}

.btn-queue-action.btn-view-invoice {
  color: #0697e4;
}

.btn-queue-action.btn-retry,
.btn-queue-action.btn-rescan {
  color: #d08700;
}

.btn-view {
  background: #0ea5e9;
  color: white;
}

.btn-view:hover {
  background: #0284c7;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

/* Success Card */
.success-card {
  margin-top: 24px;
  background: white;
  border: 3px solid var(--success);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
}

/* Consolidated Success Card */
/* A finished file is a row like the ones still working: the same height, the
   same thumbnail, the same three marks at the end. */
.success-card-consolidated {
  position: relative;
  display: flex;
  align-items: center;
  gap: 9px;
  background: white;
  border: none;
  border-radius: 6px;
  padding: 6px 8px;
  box-shadow: none;
  margin: 0;
  transition: background 0.15s ease;
}

.success-card-consolidated:hover {
  background: #fbfcfd;
}

.success-card-consolidated + .success-card-consolidated,
.success-card-consolidated + .file-item,
.file-item + .success-card-consolidated {
  border-top: 1px solid var(--gray-lighter, #f1f5f9);
}

.original-thumbnail {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 4px;
  overflow: hidden;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dde3ea;
  transition: border-color 0.15s ease;
}

.original-thumbnail:hover {
  border-color: var(--primary);
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: var(--text-light);
}

.success-info {
  flex: 1;
  min-width: 0;
}

/* What was read off the page, on two short lines instead of a striped table:
   the number with the counterparty, then the sum with the date. */
.success-head {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
}

.success-check {
  align-self: center;
  flex-shrink: 0;
  color: var(--success, #10b981);
}

.success-number {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  color: var(--text-primary);
}

.success-seller {
  flex: 1;
  min-width: 0;
  font-size: 11px;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.success-meta {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-top: 1px;
  padding-left: 19px;
  font-size: 11px;
  color: var(--text-light, #64748b);
}

.success-amount {
  font-weight: 700;
  color: var(--primary);
}

.success-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

@media (max-width: 640px) {
  .upload-zone {
    padding: 40px 20px;
  }

  .upload-buttons {
    flex-direction: column;
  }

  /* The rows stay rows on a phone: they are one line high now, and stacking
     their parts was what the 140px cards needed, not these. The counterparty
     is what gives way when there is no width for it. */
  .success-seller {
    display: none;
  }

  .success-card {
    padding: 24px 20px;
  }
}

/* Multi-Page PDF Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(8px);
  animation: fadeIn 0.3s ease;
}

.modal-content {
  background: white;
  border-radius: 12px;
  padding: 0;
  max-width: 550px;
  width: 90%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #dde3ea;
  position: relative;
}

.header-icon {
  background: #0ea5e9;
  color: white;
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.register-payment-modal {
  max-width: 480px;
}

.register-payment-modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border);
}

.register-payment-modal .modal-header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.register-payment-modal .modal-header h2 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.register-payment-modal .btn-icon {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-gray);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 0;
  padding: 0;
  flex-shrink: 0;
}

.register-payment-modal .btn-icon:hover {
  background: var(--primary-light);
  border-color: var(--primary);
  color: var(--primary);
}

.register-payment-hint {
  margin: 0 0 16px 0;
  color: var(--text-secondary);
  font-size: 14px;
}

.register-payment-modal .modal-options {
  grid-template-columns: 1fr;
}

.register-payment-modal .payment-option {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  text-align: left;
  padding: 16px;
}

.register-payment-modal .payment-info {
  flex: 1;
  min-width: 0;
}

.payment-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  flex-shrink: 0;
}

.register-payment-modal .payment-option h4,
.register-payment-modal .payment-option p {
  text-align: left;
}

.register-payment-modal .payment-option h4 {
  margin-bottom: 2px;
}

.payment-icon-blue {
  background: var(--primary, #0ea5e9);
}

.payment-icon-green {
  background: #10b981;
}

.header-content {
  flex: 1;
}

.header-content h3 {
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
}

.modal-subtitle {
  margin: 0;
  font-size: 14px;
  color: #4b5563;
}

.modal-body {
  padding: 24px;
}

/* The choice dialogue, at the dashboard's scale.
 *
 * One size for all three densities on purpose: a dialogue is read once, in
 * front of everything else, and it should not be a different size depending
 * on how tight the table behind it is set. */
.modal-choice {
  max-width: 380px;
  /* The dashboard's card: a hairline edge and a shadow soft enough that the
     edge is what draws it, not the shadow. */
  border: 1px solid #dde3ea;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.modal-choice .header-content h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
}

.modal-choice .modal-subtitle {
  margin: 1px 0 0;
  font-size: 11px;
  color: var(--text-secondary);
}

.modal-choice .modal-body {
  padding: 10px;
}

/* The close, as the quiet icon button the rest of the app uses. */
.modal-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  margin-left: auto;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

/* Hovering turns the mark blue and lifts it two pixels, with nothing drawn
   behind it — the navigation's gesture, now the app's one hover for a mark
   that is on its own. */
.modal-close:hover {
  background: transparent;
  color: var(--primary);
  transform: translateY(-2px);
}

.modal-description {
  margin: 0 0 24px 0;
  color: #4b5563;
  font-size: 15px;
  text-align: center;
  line-height: 1.6;
  font-weight: 500;
}

.modal-body strong {
  color: var(--primary);
  font-weight: 700;
}

.modal-options {
  display: grid;
  gap: 6px;
}

/* A choice reads as a row, like everything else in this app: the mark, then
   what it does, then what that means. It was a centred card with a 56px
   solid glyph over a 16px heading — three posters stacked in a dialogue. */
.modal-option-btn {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 8px;
  background: white;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
}

/* The edge turns blue and the mark with it; nothing fills. A wash the
   colour of a chosen row made hovering look like choosing. */
.modal-option-btn:hover {
  border-color: var(--primary);
  background: #fff;
}

.modal-option-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.option-text {
  display: block;
}

.modal-option-btn h4 {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary);
  transition: color 0.2s ease;
}

.modal-option-btn p {
  margin: 1px 0 0;
  font-size: 11px;
  color: var(--text-secondary);
  text-align: left;
  line-height: 1.4;
}

.option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  flex-shrink: 0;
  transition: color 0.2s ease;
  color: var(--primary);
}

.modal-option-btn:hover h4,
.modal-option-btn:hover .option-icon {
  color: var(--primary);
}

/* Mixed Mode Modal */
/* The classification sheet.
 *
 * Same card as the dashboard: a hairline edge, a soft shadow and one 12px
 * radius. The previews keep the size they had — 200px columns at a page's
 * own proportions — and everything around them is drawn thin so the pages
 * are what the eye lands on. */
.mixed-mode-modal {
  max-width: 90%;
  width: 1200px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  border: 1px solid #dde3ea;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.mixed-mode-modal .header-content h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
}

.mixed-mode-modal .modal-subtitle {
  margin: 1px 0 0;
  font-size: 11px;
  color: var(--text-secondary);
}

.mixed-mode-body {
  max-height: calc(90vh - 140px);
  overflow-y: auto;
  padding: 12px;
}

.pages-classification-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.page-classification-item {
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 8px;
  overflow: hidden;
  background: white;
  transition: border-color 0.15s ease;
}

.page-classification-item.page-excluded {
  opacity: 0.55;
  background: #fbfcfd;
}

.page-classification-item.page-excluded .page-preview-img {
  filter: grayscale(100%);
}

/* Tint and edge on hover, no lift — the same as a row in the table. */
.page-classification-item:hover {
  border-color: var(--primary);
}

.page-classification-item.page-excluded:hover {
  border-color: #4b5563;
}

.page-preview-wrapper {
  position: relative;
  aspect-ratio: 1 / 1.4;
  background: var(--bg-gray);
  overflow: hidden;
}

/* Nothing around the box: the chip it sat on made it read as two squares,
   one inside the other, where the dashboard has just the one. */
.page-checkbox-wrapper {
  position: absolute;
  top: 7px;
  left: 7px;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* The dashboard's box, drawn here too: a plain square with a thin stroked
   tick, nothing tinted and nothing typed from a font. The app's stylesheet
   rules it is overriding all carry !important. */
.page-checkbox {
  appearance: none !important;
  -webkit-appearance: none !important;
  width: 13px !important;
  height: 13px !important;
  margin: 0 !important;
  background-color: #fff !important;
  background-image: none !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 2px !important;
  box-shadow: none !important;
  cursor: pointer;
  transition: border-color 0.15s ease;
}

.page-checkbox:hover {
  border-color: #4b5563 !important;
}

.page-checkbox:checked {
  background-color: #fff !important;
  border-color: #4b5563 !important;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' fill='none' stroke='%23334155' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M2.5 6.4 4.8 8.7 9.5 3.6'/%3E%3C/svg%3E") !important;
  background-repeat: no-repeat !important;
  background-position: center !important;
  background-size: 11px 11px !important;
}

.page-checkbox::before,
.page-checkbox:checked::before {
  content: none !important;
  display: none !important;
}

.page-preview-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
}

/* Which page this is: a label on the sheet, not a black sticker over it. */
.page-number-badge {
  position: absolute;
  bottom: 6px;
  left: 6px;
  padding: 2px 6px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #dde3ea;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  color: var(--text-secondary);
}

.page-rotation-controls {
  position: absolute;
  top: 6px;
  right: 6px;
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.page-preview-wrapper:hover .page-rotation-controls {
  opacity: 1;
}

/* A touch screen never hovers, so on one they are simply there — otherwise
   a page photographed sideways could not be turned at all. */
@media (hover: none) {
  .page-rotation-controls {
    opacity: 1;
  }
}

.btn-rotate {
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid #dde3ea;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.92);
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

/* The chip stays as it is — it is what keeps the mark legible over a
   photographed page — and the hover is the mark turning blue and lifting. */
.btn-rotate:hover {
  color: var(--primary);
  transform: translateY(-2px);
}

.page-classification-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 8px;
  background: white;
  border-top: 1px solid var(--gray-lighter, #f1f5f9);
}

.classification-label {
  font-size: 10px;
  font-weight: 600;
  color: var(--text-light, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  /* The name of the field gives way before its value does. */
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* The picker: the app's quiet control with the chevron drawn on it, the same
   as the type and per-page selects on the dashboard. */
.invoice-pick {
  /* Never squeezed: it holds the number, which is the point of the card. */
  flex: 0 0 auto;
  min-width: 52px;
  margin-left: auto;
  padding: 4px 22px 4px 8px;
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 6px;
  background: #fff;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12' fill='none' stroke='%23374151' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m2.5 4.5 3.5 3.5 3.5-3.5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 7px center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.invoice-pick:hover:not(:disabled) {
  border-color: var(--primary);
}

.invoice-pick.open {
  border-color: var(--primary);
  color: var(--primary);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12' fill='none' stroke='%232b82f0' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m2.5 7.5 3.5-3.5 3.5 3.5'/%3E%3C/svg%3E");
}

.invoice-pick:disabled {
  color: var(--text-light, #64748b);
  cursor: default;
}

.classification-summary {
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #dde3ea;
  border-radius: 8px;
}

.classification-summary h4 {
  margin: 0 0 6px 0;
  font-size: 12px;
  font-weight: 700;
  color: var(--text-primary);
}

/* The groups read as a list, not as a stack of cards: as many across as fit,
   each one line. */
.summary-items {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 20px;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--text-secondary);
}

.summary-item svg {
  color: var(--primary);
  flex-shrink: 0;
}

.summary-item strong {
  color: var(--text-primary);
  font-weight: 700;
}

.modal-footer {
  padding: 10px 14px;
  border-top: 1px solid #dde3ea;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  background: white;
}

.btn-cancel-mixed,
.btn-confirm-mixed {
  padding: 6px 14px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-cancel-mixed {
  background: white;
  border: 1px solid var(--border);
  color: var(--text-primary);
}

.btn-cancel-mixed:hover {
  border-color: var(--danger);
  color: var(--danger);
}

.btn-confirm-mixed {
  background: var(--primary);
  border: 1px solid var(--primary);
  color: white;
}

.btn-confirm-mixed:hover:not(:disabled) {
  background: var(--primary-hover);
}

.btn-confirm-mixed:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* The invoice-number menu: the same white panel as every other dropdown in
   the app, in the body so the card it belongs to cannot clip it. */
.invoice-menu {
  position: fixed;
  /* Over the overlay, which sits at 9999 — the menu is teleported to the
     body, so it is that z-index it has to clear, not the card's. */
  z-index: 10050;
  max-height: 240px;
  padding: 4px;
  background: #fff;
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
}

.invoice-menu-item {
  display: block;
  width: 100%;
  padding: 5px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-primary);
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease;
}

.invoice-menu-item:hover {
  background: var(--primary-light, #e0f2fe);
}

.invoice-menu-item.selected {
  color: var(--primary);
  font-weight: 700;
}

/* Starting a new invoice is a different kind of action, so it sits under a
   line rather than in the run of numbers. */
.invoice-menu-item--new {
  margin-top: 4px;
  padding-top: 7px;
  border-top: 1px solid var(--gray-lighter, #f1f5f9);
  border-radius: 0 0 6px 6px;
  color: var(--primary);
  font-weight: 600;
}

.loading-spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@media (max-width: 640px) {
  .modal-content {
    width: 95%;
    margin: 20px;
  }

  /* The two redrawn dialogues are excluded: they are set at one compact size
     for every screen and density, and these were loosening them back to the
     old poster proportions on a phone. */
  .modal-content:not(.modal-choice):not(.mixed-mode-modal) .modal-header,
  .modal-content:not(.modal-choice):not(.mixed-mode-modal) .modal-body {
    padding: 20px;
  }

  .modal-content:not(.modal-choice) .modal-option-btn {
    padding: 16px;
  }

  /* Full bleed on a phone, so no edge and no corners to draw. */
  .mixed-mode-modal {
    max-width: 100%;
    width: 100%;
    margin: 0;
    max-height: 100vh;
    border: none;
    border-radius: 0;
  }

  /* Two across: the pages are what is being judged here, and one per row
     would mean scrolling past a screenful for every page. */
  .pages-classification-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  /* The label above the picker, not beside it — side by side there is room
     for about three characters of the invoice number. */
  .page-classification-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 3px;
  }

  .invoice-pick {
    width: 100%;
  }
}

/* ── The dashboard's dress ─────────────────────────────────────────────
 *
 * One card, one 12px radius, hairline edges, and controls at the table's
 * scale: 13px labels in 32px boxes, one filled button and the rest quiet.
 * A dialogue is opened from the table and should read as the same product.
 *
 * Last in the file on purpose — these do not delete what the modal set for
 * itself above, they settle the frame, the heading and the buttons. */
.modal-content,
.modal-container,
.modal-content-large {
    border: 1px solid #dde3ea;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.modal-header {
    padding: 12px 14px;
    border-bottom: 1px solid #dde3ea;
    background: #fff;
}

.modal-header h2,
.modal-header h3,
.modal-header h4,
.modal-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.modal-subtitle {
    margin: 1px 0 0;
    font-size: 11px;
    font-weight: 400;
    color: #4b5563;
}

.modal-body {
    padding: 12px 14px;
}

.modal-footer {
    padding: 10px 14px;
    gap: 8px;
    background: #fff;
    border-top: 1px solid #dde3ea;
}

/* The close: a mark on its own, with the lift the rest of the app has. */
.modal-header .btn-icon,
.btn-close,
.modal-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    min-width: 26px;
    padding: 0;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    color: #4b5563;
    box-shadow: none;
    transition: all 0.15s ease;
}

.modal-header .btn-icon:hover,
.btn-close:hover,
.modal-close:hover {
    background: transparent;
    color: #0697e4;
    transform: translateY(-2px);
}

/* The buttons: the same box everywhere, filled only where something is
   being committed. */
.btn-primary,
.btn-secondary,
.btn-cancel,
.btn-cancel-edit,
.btn-save,
.btn-save-edit,
.btn-confirm,
.btn-danger,
.btn-success,
.btn-add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 32px;
    padding: 0 14px;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    box-shadow: none;
    transition: all 0.15s ease;
}

.btn-secondary,
.btn-cancel,
.btn-cancel-edit {
    background: #fff;
    border: 1px solid #dde3ea;
    color: #1e293b;
}

.btn-secondary:hover:not(:disabled),
.btn-cancel:hover:not(:disabled),
.btn-cancel-edit:hover:not(:disabled) {
    background: #fff;
    border-color: #0697e4;
    color: #0697e4;
    box-shadow: none;
    transform: none;
}

.btn-primary,
.btn-confirm,
.btn-save,
.btn-save-edit,
.btn-add {
    background: #0697e4;
    border: 1px solid #0697e4;
    color: #fff;
}

.btn-primary:hover:not(:disabled),
.btn-confirm:hover:not(:disabled),
.btn-save:hover:not(:disabled),
.btn-save-edit:hover:not(:disabled),
.btn-add:hover:not(:disabled) {
    background: #0b84c7;
    border-color: #0b84c7;
    box-shadow: none;
    transform: none;
}

.btn-success {
    background: #10b981;
    border: 1px solid #10b981;
    color: #fff;
}

.btn-success:hover:not(:disabled) {
    background: #0ea371;
    border-color: #0ea371;
    box-shadow: none;
    transform: none;
}

.btn-danger {
    background: #e04545;
    border: 1px solid #e04545;
    color: #fff;
}

.btn-danger:hover:not(:disabled) {
    background: #c93a3a;
    border-color: #c93a3a;
    box-shadow: none;
    transform: none;
}

/* The row of answers under a question — "delete these seven?" — is a footer
   like any other: a hairline above it and the two buttons at its end. */
.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 10px 14px;
    border-top: 1px solid #dde3ea;
    background: #fff;
}

/* The "what are you photographing" sheet, in the same dress as the choice
   dialogue it stands in for: one 12px card, hairline edge, rows not posters. */
.doc-chooser {
  max-width: 380px;
  padding: 10px;
  border: 1px solid #dde3ea;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.doc-chooser-title {
  margin: 2px 0 8px;
  font-size: 14px;
  font-weight: 700;
  text-align: left;
}

.doc-choice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 8px;
  text-align: left;
}

.doc-choice + .doc-choice {
  margin-top: 6px;
}

.doc-choice:hover {
  border-color: var(--primary, #0697e4);
  background: #fff;
  transform: none;
  box-shadow: none;
}

.doc-choice-icon {
  width: auto;
  height: auto;
  min-width: 0;
  margin: 0;
  padding: 0;
  background: transparent;
  border-radius: 0;
  color: var(--primary, #0697e4);
  font-size: inherit;
}

.doc-choice-icon svg {
  width: 18px;
  height: 18px;
}

.doc-choice-name {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
}

.doc-choice-desc {
  font-size: 11px;
  color: #4b5563;
}

/* The corners.
 *
 * The card is rounded but does not clip, so the header's own white — and the
 * footer's — was painted square and stood a pixel or two outside the curve.
 * The bands carry the same radius on the side they sit on. Clipping the card
 * instead would cut off anything a dialogue drops below its edge. */
.modal-header {
    border-radius: 12px 12px 0 0;
}

.modal-footer,
.modal-actions {
    border-radius: 0 0 12px 12px;
}

/* ── One hover for a mark on its own ───────────────────────────────────
 *
 * Wherever a button is just an icon — a row's edit or delete, a field's
 * helper, a dialogue's close — hovering turns the mark its own colour and
 * lifts it two pixels. Nothing is drawn behind it. Written last, and the
 * danger variant written with both of its classes, because the rules being
 * answered are themselves .btn-icon.btn-icon-danger. */
.btn-icon:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: var(--primary, #0697e4);
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon.btn-icon-danger:hover,
.btn-icon.btn-icon-danger:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: #e04545;
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon svg {
  transition: transform 0.15s ease;
}
</style>
